import { getAppConfigSingleton } from "@expert/app-config";
import { useNotification } from "@expert/common-ui";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { useVerifiedCustomer } from "@expert/sales-tools";
import { Group } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { type JSX, useCallback, useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { updateCase } from "@/customer-info/api/updateCase";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { isVoiceTask, useAgentSdk, useAgentStore, useCallbackState, usePartner, useSession } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";

import { getSessionSummary } from "../api/getSessionSummary";
import { useSimplrSummaryRouting } from "../api/useSimplrSummaryRouting";
import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import { EditSessionSummaryModal } from "./EditSessionSummaryModal";
import styles from "./EditSessionSummaryModal.module.css";
import { adaptSimplifiedSummary } from "./utils";

// TODO: Rename this component because it's not deprecated, and actually used by every partner except Verizon
export function DeprecatedSessionSummary(): JSX.Element | null {
    const logger = useLogger({ module: "SessionSummary", supportTeam: "solve" });
    const appConfig = getAppConfigSingleton();
    const activeSession = useSession();

    const customer = useVerifiedCustomer(activeSession.id);
    const { accountNumber, metadata: { caseNumber, caseId } = {} } = customer ?? {};
    const agentSdk = useAgentSdk();
    const partner = usePartner();
    const { accountNumberLength, wrappingAgentActivity, gaiaSummaryModel } = appConfig.getPartnersConfig(partner);
    const { simplifiedSummary } = usePartnerFeatureConfigured();
    const {
        features: { isAutoSummaryEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const { summaryConfig, isSimplrSummaryFlagsLoading } = useSimplrSummaryRouting();

    const validAccountNumber = simplifiedSummary
        ? !!caseId
        : accountNumberLength
          ? accountNumber?.length === accountNumberLength
          : false;

    const autogeneratedSummary = useSessionSummaryStore((state) => state.autogeneratedSummary);

    const [summaryLoaded, setSummaryLoaded] = useState(false);
    const [sendingSummary, setSendingSummary] = useState(false);

    const notification = useNotification();
    const [opened, { open: openModal, close: closeModal }] = useDisclosure(false);

    const agentActivity = useAgentStore(useShallow((state) => state.activity));
    const callbackState = useCallbackState();

    const { summary, submitted, subject, category, subcategory } = useSessionSummaryStore(
        useShallow((state) => ({
            summary: state.summary,
            submitted: state.submitted,
            subject: state.subject,
            category: state.category,
            subcategory: state.subcategory,
        })),
    );
    const activeTask = activeSession.currentTask;

    const agentCallSid =
        activeTask && isVoiceTask(activeTask)
            ? (activeTask.agentCallId ??
              Array.from(activeSession.tasks.values())
                  .filter(isVoiceTask)
                  .findLast((task) => task.agentCallId !== undefined)?.agentCallId)
            : undefined;

    const shouldAutoGenerateSummary = activeTask?.locale !== "es-mx" && !!gaiaSummaryModel;

    const hasActiveCustomerSessionWrappingState =
        activeSession.kind === "with-customer" && !!activeSession.wrappingState;
    // A wrapping task or a newly wrapping agent means we are entering session summary
    const inWrappingState =
        activeTask?.status === "wrapping" ||
        agentActivity === wrappingAgentActivity ||
        hasActiveCustomerSessionWrappingState;

    const taskIsActive = activeTask?.status === "assigned";
    const hasImmediateCallBack = callbackState?.callbackType === "CallbackNow";

    const handleSimplifiedEndSession = async () => {
        try {
            if (!caseId) {
                await navigator.clipboard.writeText(summary);
                notification.success("Summary copied to clipboard");
                return;
            }
            setSendingSummary(true);
            await updateCase(logger, {
                caseId,
                subject,
                description: summary,
                category,
                subcategory,
            });
            notification.success("Summary sent");
            useSessionSummaryStore.getState().setSubmitted(true);
        } catch (error) {
            logger.warn({ error }, "DeprecatedSessionSummary | Error sending summary");
            notification.error("Error sending summary. Please copy the summary manually.");
        } finally {
            setSendingSummary(false);
        }
    };

    const fetchAndSetSessionSummary = useCallback(async () => {
        logger.trace(
            { agentCallSid, sessionId: activeSession.id, partner, summarySource: summaryConfig.source },
            "DeprecatedSessionSummary | Session summary fetch check",
        );
        const generatedSummary: string = await getSessionSummary(
            logger,
            { callSid: agentCallSid, sessionId: activeSession.id, partner },
            gaiaSummaryModel,
            summaryConfig,
        );

        if (!generatedSummary) {
            logger.warn("DeprecatedSessionSummary | Received empty session summary");
        } else if (simplifiedSummary) {
            // adaptSimplifiedSummary JSON.parses GAIA's structured payload. A Simplr-sourced summary
            // (live cutover) is plain text, so parsing it rejects; without this guard that rejection
            // escapes the effect's floating promise and setSummaryLoaded never runs, stranding the
            // wrap-up modal in its loading state. Use the summary verbatim instead.
            try {
                const { subject, summary, category, subcategory } = adaptSimplifiedSummary(generatedSummary);
                const { setSessionSummary, setSubject, setCategory, setSubcategory } =
                    useSessionSummaryStore.getState();
                setSessionSummary({ value: summary, autoGenerated: true });
                setSubject(subject);
                if (category) setCategory(category);
                if (subcategory) setSubcategory(subcategory);
            } catch (error) {
                logger.warn(
                    { error, summarySource: summaryConfig.source },
                    "DeprecatedSessionSummary | Could not adapt simplified summary; using it verbatim",
                );
                useSessionSummaryStore.getState().setSessionSummary({ value: generatedSummary, autoGenerated: true });
            }
        } else {
            useSessionSummaryStore.getState().setSessionSummary({ value: generatedSummary, autoGenerated: true });
        }
        setSummaryLoaded(true);
    }, [agentCallSid, activeSession.id, partner, gaiaSummaryModel, simplifiedSummary, logger, summaryConfig]);

    useEffect(() => {
        if (!shouldAutoGenerateSummary) {
            setSummaryLoaded(true);
            return;
        }

        // Wait for the Simplr routing flags: fetching before they resolve silently pins the summary
        // to GAIA regardless of the flags' actual value (same gate as SessionSummary.tsx).
        if (opened && inWrappingState && !summaryLoaded && !isSimplrSummaryFlagsLoading) {
            if (autogeneratedSummary && !submitted && simplifiedSummary) {
                setSummaryLoaded(true);
                if (caseId) {
                    void handleSimplifiedEndSession();
                }
            } else if (!autogeneratedSummary || !summaryLoaded) {
                // NOTE: The summary loaded check is to prevent getting stuck in the loading state if we've already generated a summary from a previous leg of the call
                void fetchAndSetSessionSummary();
            }
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [
        inWrappingState,
        shouldAutoGenerateSummary,
        opened,
        autogeneratedSummary,
        submitted,
        simplifiedSummary,
        isSimplrSummaryFlagsLoading,
    ]);

    useEffect(() => {
        if (hasImmediateCallBack) return;

        if (inWrappingState) {
            openModal();
        } else if (taskIsActive) {
            useSessionSummaryStore.getState().setCanAppend(true);
        }
    }, [hasImmediateCallBack, inWrappingState, taskIsActive, openModal]);

    const shouldSendSummary = !submitted && summary;

    async function handleWrappingExpired() {
        await handleEndSession("WrappingTimeExpired");
    }

    const handleCopyAndSendSummary = useCallback(async () => {
        async function writeToClipboard() {
            try {
                await navigator.clipboard.writeText(summary);
            } catch (error) {
                logger.warn({ error }, "DeprecatedSessionSummary | Error copying session summary");
            }
        }
        await writeToClipboard();
    }, [logger, summary]);

    const handleEndSession = useCallback(
        async (reason: "AgentTerminatedWrapping" | "WrappingTimeExpired") => {
            if (validAccountNumber && isAutoSummaryEnabled) {
                logger.trace(
                    { isAutoSummaryEnabled, partner, reason, validAccountNumber },
                    "DeprecatedSessionSummary | Ending session wrapup",
                );
            } else await handleCopyAndSendSummary();
            logger.trace({ reason, partner }, "DeprecatedSessionSummary | Ending session wrapup");
            await agentSdk.endWrapup(reason, partner, activeSession.id, activeTask ?? undefined);
        },
        [
            activeSession.id,
            activeTask,
            agentSdk,
            handleCopyAndSendSummary,
            isAutoSummaryEnabled,
            logger,
            partner,
            validAccountNumber,
        ],
    );

    const sendSummary = useCallback(async () => {
        notification.success("Session summary copied to clipboard");
        try {
            await navigator.clipboard.writeText(summary);
        } catch (error) {
            logger.warn({ error }, "DeprecatedSessionSummary | Error copying session summary");
        }
    }, [logger, summary, notification]);

    const onDismiss = useCallback(
        async ({ checkSummary = true } = {}) => {
            logger.trace("DeprecatedSessionSummary | Session summary dismissed");
            closeModal();
            if (checkSummary && shouldSendSummary) {
                await sendSummary();
            }
        },
        [closeModal, logger, sendSummary, shouldSendSummary],
    );

    return (
        <Group className={styles.tempTimeline}>
            <EditSessionSummaryModal
                loading={!summaryLoaded && shouldAutoGenerateSummary}
                onDismiss={simplifiedSummary ? () => {} : () => void onDismiss()}
                sendingSummary={sendingSummary}
                onExpired={simplifiedSummary ? () => {} : handleWrappingExpired}
                simplifiedSummary={simplifiedSummary}
                caseNumber={caseNumber}
                validAccountNumber={validAccountNumber}
                endSession={simplifiedSummary ? handleSimplifiedEndSession : handleEndSession}
                opened={opened}
            />
        </Group>
    );
}
